import type { SupabaseClient } from "@supabase/supabase-js";
import type { AppEnv } from "../config/env";
import { sanitizeForTelemetry } from "../errors/redaction";
import type { ObservabilityClient } from "../observability/types";
import type { Database } from "../types/database";

export type HealthStatus = "healthy" | "degraded" | "unhealthy" | "unknown";

export type HealthCheckResult = {
  component: string;
  status: HealthStatus;
  message: string;
  latencyMs: number;
  checkedAt: string;
  metadata?: Record<string, unknown>;
};

async function measure(
  component: string,
  check: () => Promise<
    Omit<HealthCheckResult, "component" | "latencyMs" | "checkedAt">
  >,
): Promise<HealthCheckResult> {
  const started = performance.now();
  const checkedAt = new Date().toISOString();

  try {
    const result = await check();

    return {
      component,
      checkedAt,
      latencyMs: Math.round(performance.now() - started),
      ...result,
      metadata: sanitizeForTelemetry(result.metadata ?? {}) as Record<
        string,
        unknown
      >,
    };
  } catch (error) {
    return {
      component,
      checkedAt,
      latencyMs: Math.round(performance.now() - started),
      status: "unhealthy",
      message: "Health check failed.",
      metadata: sanitizeForTelemetry({ error }) as Record<string, unknown>,
    };
  }
}

export async function runSystemHealthChecks(input: {
  appEnv: AppEnv;
  supabase: SupabaseClient<Database>;
  observability: ObservabilityClient;
  ownerEmail?: string;
}): Promise<HealthCheckResult[]> {
  return Promise.all([
    measure("application_startup", async () => ({
      status: "healthy",
      message: "Application startup completed.",
      metadata: { environment: input.appEnv.VITE_APP_ENV },
    })),
    measure("environment_configuration", async () => ({
      status: "healthy",
      message: "Browser-safe environment configuration is valid.",
      metadata: { environment: input.appEnv.VITE_APP_ENV },
    })),
    measure("supabase_client", async () => ({
      status: "healthy",
      message: "Supabase client initialized after environment validation.",
    })),
    measure("supabase_auth", async () => {
      const { error } = await input.supabase.auth.getSession();

      return {
        status: error ? "degraded" : "healthy",
        message: error
          ? "Supabase auth session check returned an error."
          : "Supabase auth session API responded.",
        metadata: error ? { error } : undefined,
      };
    }),
    measure("database_connectivity", async () => {
      const { error } = await input.supabase
        .from("profiles")
        .select("id")
        .limit(1);

      return {
        status: error ? "degraded" : "healthy",
        message: error
          ? "Database connectivity check could not read profiles."
          : "Database connectivity check completed.",
        metadata: error ? { error } : undefined,
      };
    }),
    measure("observability_persistence", async () => {
      await input.observability.event({
        category: "health",
        eventName: "observability_health_check",
        message: "Observability health check executed.",
      });

      return {
        status: "healthy",
        message: "Observability pipeline accepted a health event.",
      };
    }),
  ]);
}
